/**
 * Morning Howl (Operations Hub): the daily stand-up board. KB morning-howl.md.
 *
 * A team member clicks "Randomize who goes first"; after that each speaker picks
 * who goes next (click their name) and people are checked off as they go. The
 * Stay After panel lists who's asked to stick around, and with whom.
 *
 * The board is shared (services/agent/app/howl_routes.py): every write returns
 * the new board, and the page polls so everyone with it open sees the same thing.
 * Rebuilt from the owner's single-file prototype in Omega Wolf's brand, simpler:
 * no sounds, no keyboard roulette, no per-browser storage.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarCheck,
  faCheck,
  faPen,
  faRotateLeft,
  faShuffle,
  faThumbtack,
  faUserSlash,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { DWA } from '../theme/dwaTokens';
import { Tooltip } from '../hubs/Tooltip';
import { GradientPage } from '../hubs/HubCards';
import { HubLayout } from '../hubs/HubHeader';
import {
  checkCalendar,
  clearStayAfter,
  fetchHowl,
  pickNext,
  resetHowl,
  saveRoster,
  setOut,
  setStayAfter,
  startHowl,
  undoTurn,
  uncheckTurn,
  type HowlBoard,
} from '../services/howlApi';
import {
  isStayingAfter,
  calendarOk,
  joinNames,
  memberStatus,
  rosterLine,
  nameOf,
  parseRoster,
  progress,
  turnNumber,
  type MemberStatus,
} from './howlBoard';

export const MORNING_HOWL_ROUTE = '/morning-howl';
const POLL_MS = 3000;

const card: React.CSSProperties = {
  background: DWA.cardBg,
  borderRadius: DWA.radius,
  border: '1px solid rgba(255,255,255,0.3)',
  boxShadow: DWA.cardShadow,
  color: DWA.ink,
  padding: '1.1rem 1.15rem',
  minWidth: 0,
};

const goldButton: React.CSSProperties = {
  padding: '0.55rem 1.1rem',
  backgroundColor: DWA.gold,
  color: DWA.onGold,
  border: 'none',
  borderRadius: DWA.radius,
  fontSize: '0.85rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: DWA.font,
  whiteSpace: 'nowrap',
};

const ghostButton: React.CSSProperties = {
  ...goldButton,
  background: 'transparent',
  color: DWA.ink,
  border: `1px solid ${DWA.cardBorder}`,
};

const linkButton: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '0.25rem 0.4rem',
  color: DWA.textSecondary,
  fontSize: '0.8rem',
  fontFamily: DWA.font,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
};

const sectionTitle: React.CSSProperties = {
  margin: 0,
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: DWA.textSecondary,
};

function formatDay(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// ─── speaker plate ───────────────────────────────────────────────────────────

const Plate: React.FC<{
  board: HowlBoard;
  rolling: string | null;
  busy: boolean;
  onStart: () => void;
  onUndo: () => void;
  onReset: () => void;
  onCheckCalendar: () => void;
}> = ({ board, rolling, busy, onStart, onUndo, onReset, onCheckCalendar }) => {
  const { done, total } = progress(board);
  const current = nameOf(board, board.current_member_id);
  let label = 'Ready to Howl';
  let name: string = rolling ?? '';
  let hint = 'When the team is ready, randomize who goes first, or click whoever goes first.';
  if (rolling) {
    label = 'Picking who goes first…';
    hint = '';
  } else if (board.finished) {
    label = "That's the whole pack";
    name = 'Everyone has howled';
    hint = board.stay_after.length ? 'Check the Stay After list before everyone drops off.' : 'Nobody is staying after today.';
  } else if (board.started) {
    label = board.turns.length === 1 ? 'Going first' : 'Now howling';
    name = current;
    hint = `${current}, pick who goes next: click their name below.`;
  }
  return (
    <section
      aria-live="polite"
      style={{ ...card, padding: 'clamp(1.25rem, 3vw, 2rem)', textAlign: 'center', borderTop: `4px solid ${DWA.gold}` }}
    >
      <p style={{ ...sectionTitle, color: DWA.goldInk }}>{label}</p>
      {name && (
        <p
          data-testid="howl-current"
          style={{
            margin: '0.6rem 0 0',
            fontSize: 'clamp(2rem, 7vw, 3.6rem)',
            fontWeight: 800,
            lineHeight: 1.05,
            color: rolling ? DWA.textSecondary : DWA.ink,
            overflowWrap: 'anywhere',
          }}
        >
          {name}
        </p>
      )}
      {!board.started && !rolling && (
        <button
          type="button"
          onClick={onStart}
          disabled={busy || board.waiting_member_ids.length === 0}
          style={{
            ...goldButton,
            marginTop: '0.9rem',
            padding: '0.85rem 1.6rem',
            fontSize: '1rem',
            opacity: busy || board.waiting_member_ids.length === 0 ? 0.6 : 1,
          }}
        >
          <FontAwesomeIcon icon={faShuffle} style={{ marginRight: '0.5rem' }} />
          Randomize who goes first
        </button>
      )}
      {hint && <p style={{ margin: '0.75rem 0 0', color: DWA.textSecondary, fontSize: '0.9rem' }}>{hint}</p>}
      <div
        style={{
          marginTop: '1rem',
          paddingTop: '0.75rem',
          borderTop: `1px solid ${DWA.lineLight}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
          flexWrap: 'wrap',
        }}
      >
        <span data-testid="howl-progress" style={{ fontSize: '0.85rem', color: DWA.textSecondary }}>
          <strong style={{ color: DWA.ink }}>{done}</strong> of <strong style={{ color: DWA.ink }}>{total}</strong> checked off
          {board.out_member_ids.length > 0 && ` · ${board.out_member_ids.length} out today`}
        </span>
        <span style={{ display: 'inline-flex', gap: '0.25rem' }}>
          <button type="button" style={linkButton} onClick={onUndo} disabled={busy || !board.started}>
            <FontAwesomeIcon icon={faRotateLeft} /> Undo last
          </button>
          <button type="button" style={linkButton} onClick={onReset} disabled={busy}>
            <FontAwesomeIcon icon={faXmark} /> Reset meeting
          </button>
        </span>
      </div>
      <CalendarLine board={board} busy={busy} onCheck={onCheckCalendar} />
    </section>
  );
};

/**
 * Who's-out from Google Calendar: when it last checked, and a check-now button.
 * Never says who was marked out by it or why (Jon, 2026-11-03).
 */
const CalendarLine: React.FC<{ board: HowlBoard; busy: boolean; onCheck: () => void }> = ({ board, busy, onCheck }) => {
  const cal = board.calendar;
  const when = cal?.checked_at ? new Date(cal.checked_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : null;
  return (
    <p data-testid="howl-calendar" style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: DWA.textMuted, textAlign: 'left' }}>
      <FontAwesomeIcon icon={faCalendarCheck} aria-hidden="true" style={{ marginRight: '0.35rem' }} />
      {!cal?.status
        ? 'Checking Google Calendar for who’s out…'
        : calendarOk(cal.status)
          ? `Calendar checked ${when ?? ''}: anyone out today is marked out.`
          : cal.status}
      <button type="button" style={{ ...linkButton, fontSize: '0.75rem', color: DWA.goldInk, fontWeight: 700 }} disabled={busy} onClick={onCheck}>
        Check now
      </button>
    </p>
  );
};

// ─── roster ──────────────────────────────────────────────────────────────────

const CHIP_STYLES: Record<MemberStatus, React.CSSProperties> = {
  waiting: { background: '#ffffff', border: `1px solid ${DWA.lineLight}`, color: DWA.ink },
  now: { background: DWA.goldSoft, border: `2px solid ${DWA.gold}`, color: DWA.ink, fontWeight: 800 },
  done: { background: '#f3f4f6', border: `1px dashed ${DWA.lineLight}`, color: DWA.textSecondary },
  out: { background: '#f3f4f6', border: `1px dashed ${DWA.lineLight}`, color: DWA.textMuted, textDecoration: 'line-through' },
};

const Chip: React.FC<{
  board: HowlBoard;
  memberId: number;
  name: string;
  flashing: boolean;
  busy: boolean;
  onPick: (id: number) => void;
  onUncheck: (id: number) => void;
  onToggleStay: (id: number, stay: boolean) => void;
  onToggleOut: (id: number, out: boolean) => void;
}> = ({ board, memberId, name, flashing, busy, onPick, onUncheck, onToggleStay, onToggleOut }) => {
  const status = memberStatus(board, memberId);
  const outable = status === 'waiting' || status === 'out';
  // The circle always works (Jon, 2026-11-03): an empty one checks the person off as
  // next (first, before the random pick); a checked one un-checks them.
  const pickable = status === 'waiting' && !busy;
  const uncheckable = (status === 'done' || status === 'now') && !busy;
  const circleTip = pickable
    ? board.started ? `Check ${name} off: they go next` : `${name} goes first`
    : uncheckable ? `Un-check ${name}` : status === 'out' ? `${name} is out today` : '';
  const staying = isStayingAfter(board, memberId);
  const n = turnNumber(board, memberId);
  return (
    <div>
      <div
        data-testid={`howl-chip-${memberId}`}
        data-status={status}
        style={{
          ...CHIP_STYLES[status],
          ...(flashing ? { background: DWA.goldSoft, borderColor: DWA.gold } : {}),
          borderRadius: DWA.radius,
          display: 'flex',
          alignItems: 'stretch',
          boxShadow: staying ? `inset 4px 0 0 ${DWA.gold}` : undefined,
          minHeight: 48,
        }}
      >
        <button
          type="button"
          onClick={() => (pickable ? onPick(memberId) : uncheckable && onUncheck(memberId))}
          disabled={!pickable && !uncheckable}
          aria-pressed={status === 'done' || status === 'now'}
          title={circleTip || undefined}
          style={{
            flex: 1,
            minWidth: 0,
            background: 'none',
            border: 'none',
            padding: '0.65rem 0.4rem 0.65rem 0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'inherit',
            font: 'inherit',
            fontSize: '0.95rem',
            fontWeight: status === 'now' ? 800 : 600,
            textDecoration: 'inherit',
            cursor: pickable || uncheckable ? 'pointer' : 'default',
            textAlign: 'left',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 20,
              height: 20,
              flex: '0 0 auto',
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.65rem',
              background: status === 'done' ? DWA.positive : status === 'now' ? DWA.gold : 'transparent',
              color: status === 'done' ? '#fff' : DWA.onGold,
              border: status === 'waiting' ? `1.5px solid ${DWA.textMuted}` : 'none',
            }}
          >
            {status === 'done' && <FontAwesomeIcon icon={faCheck} />}
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          {status === 'now' && <span style={{ fontSize: '0.7rem', color: DWA.goldInk, letterSpacing: '0.1em' }}>NOW</span>}
          {status === 'done' && n && <span style={{ fontSize: '0.7rem', color: DWA.textMuted }}>#{n}</span>}
          {status === 'out' && <span style={{ fontSize: '0.7rem', textDecoration: 'none' }}>out</span>}
        </button>
        <ChipIcon
          tip={staying ? `${name} is staying after: click to remove (edit "with whom" in Stay After)` : `${name} stays after`}
          label={`Stay after: ${name}`}
          icon={faThumbtack}
          active={staying}
          disabled={busy}
          onClick={() => onToggleStay(memberId, !staying)}
        />
        <ChipIcon
          tip={
            status === 'out'
              ? `${name} is out today. Click if they're here.`
              : outable
                ? `Mark ${name} out today (skipped by the random pick)`
                : `${name} has already gone today`
          }
          label={status === 'out' ? `Back in today: ${name}` : `Out today: ${name}`}
          icon={faUserSlash}
          active={status === 'out'}
          disabled={!outable || busy}
          onClick={() => onToggleOut(memberId, status !== 'out')}
        />
      </div>
    </div>
  );
};

/** A small icon action on a roster chip, explained by its tooltip. */
const ChipIcon: React.FC<{
  tip: string;
  label: string;
  icon: typeof faThumbtack;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ tip, label, icon, active, disabled, onClick }) => (
  <Tooltip content={tip} placement="bottom" delay={200}>
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: '0 0.5rem',
        cursor: disabled ? 'default' : 'pointer',
        color: active ? DWA.goldInk : DWA.textMuted,
        opacity: disabled ? 0.35 : 1,
        fontSize: '0.85rem',
      }}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  </Tooltip>
);

// ─── dialogs ─────────────────────────────────────────────────────────────────

const Dialog: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div role="dialog" aria-modal="true" aria-label={title} style={{ ...card, width: 'min(460px, 100%)', maxHeight: '90vh', overflow: 'auto' }}>
        <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.1rem', fontWeight: 700 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
};

const StayAfterDialog: React.FC<{
  board: HowlBoard;
  memberId: number | null;
  busy: boolean;
  onSave: (memberId: number, withIds: number[], note: string) => void;
  onClose: () => void;
}> = ({ board, memberId, busy, onSave, onClose }) => {
  const existing = board.stay_after.find((s) => s.member_id === memberId);
  const [who, setWho] = useState<number | null>(memberId);
  const [withIds, setWithIds] = useState<number[]>(existing?.with_member_ids ?? []);
  const [note, setNote] = useState(existing?.note ?? '');
  return (
    <Dialog title="Stay after" onClose={onClose}>
      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.3rem' }} htmlFor="howl-who">
        Who's staying after?
      </label>
      <select
        id="howl-who"
        value={who ?? ''}
        onChange={(e) => {
          const id = Number(e.target.value) || null;
          setWho(id);
          const row = board.stay_after.find((s) => s.member_id === id);
          setWithIds(row?.with_member_ids ?? []);
          setNote(row?.note ?? '');
        }}
        style={{ width: '100%', padding: '0.5rem', borderRadius: DWA.radius, border: `1px solid ${DWA.lineLight}`, fontFamily: DWA.font }}
      >
        <option value="">Choose a teammate</option>
        {board.members.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>
      <p style={{ fontSize: '0.85rem', fontWeight: 700, margin: '0.9rem 0 0.3rem' }}>With</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '0.3rem' }}>
        {board.members
          .filter((m) => m.id !== who)
          .map((m) => (
            <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem' }}>
              <input
                type="checkbox"
                checked={withIds.includes(m.id)}
                onChange={(e) => setWithIds((cur) => (e.target.checked ? [...cur, m.id] : cur.filter((x) => x !== m.id)))}
              />
              {m.name}
            </label>
          ))}
      </div>
      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, margin: '0.9rem 0 0.3rem' }} htmlFor="howl-note">
        About (optional)
      </label>
      <input
        id="howl-note"
        value={note}
        maxLength={200}
        onChange={(e) => setNote(e.target.value)}
        placeholder="e.g. PLC launch timeline"
        style={{ width: '100%', padding: '0.5rem', borderRadius: DWA.radius, border: `1px solid ${DWA.lineLight}`, fontFamily: DWA.font, boxSizing: 'border-box' }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.1rem' }}>
        <button type="button" style={ghostButton} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          style={{ ...goldButton, opacity: who && !busy ? 1 : 0.6 }}
          disabled={!who || busy}
          onClick={() => who && onSave(who, withIds, note)}
        >
          Save
        </button>
      </div>
    </Dialog>
  );
};

const RosterDialog: React.FC<{ board: HowlBoard; busy: boolean; onSave: (names: string[]) => void; onClose: () => void }> = ({
  board,
  busy,
  onSave,
  onClose,
}) => {
  const [text, setText] = useState(board.members.map(rosterLine).join('\n'));
  return (
    <Dialog title="The pack" onClose={onClose}>
      <p style={{ margin: '0 0 0.6rem', color: DWA.textSecondary, fontSize: '0.85rem' }}>
        One person per line, in the order you want them shown, as <code>Name &lt;work email&gt;</code>. The email lets the
        Google Calendar check find them. Saved for everyone.
      </p>
      <textarea
        aria-label="Team members, one per line"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={12}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '0.6rem',
          borderRadius: DWA.radius,
          border: `1px solid ${DWA.lineLight}`,
          fontFamily: DWA.font,
          fontSize: '0.95rem',
          lineHeight: 1.6,
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
        <button type="button" style={ghostButton} onClick={onClose}>
          Cancel
        </button>
        <button type="button" style={{ ...goldButton, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={() => onSave(parseRoster(text))}>
          Save pack
        </button>
      </div>
    </Dialog>
  );
};

// ─── page ────────────────────────────────────────────────────────────────────

export const MorningHowlBoard: React.FC = () => {
  const [board, setBoard] = useState<HowlBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rolling, setRolling] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<number | null>(null);
  const [stayFor, setStayFor] = useState<number | null | 'new'>(null);
  const [editingRoster, setEditingRoster] = useState(false);
  const busyRef = useRef(false);

  const load = useCallback(() => {
    if (busyRef.current) return;
    fetchHowl()
      .then((b) => {
        if (!busyRef.current) setBoard(b);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const run = useCallback(async (action: () => Promise<HowlBoard>): Promise<HowlBoard | null> => {
    busyRef.current = true;
    setBusy(true);
    try {
      const next = await action();
      setBoard(next);
      setError(null);
      return next;
    } catch (e) {
      setError((e as Error).message);
      return null;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, []);

  /** The server picks; the page plays a short name shuffle before showing it. */
  const onStart = useCallback(async () => {
    if (!board) return;
    const pool = board.members.filter((m) => board.waiting_member_ids.includes(m.id));
    busyRef.current = true;
    setBusy(true);
    let result: HowlBoard | null = null;
    try {
      result = await startHowl();
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
    if (result && pool.length > 1 && !reducedMotion()) {
      for (let i = 0; i < 14; i += 1) {
        const m = pool[Math.floor(Math.random() * pool.length)];
        setRolling(m.name);
        setFlashId(m.id);
        await new Promise((r) => setTimeout(r, 60 + i * i * 2));
      }
    }
    setRolling(null);
    setFlashId(null);
    if (result) setBoard(result);
    busyRef.current = false;
    setBusy(false);
  }, [board]);

  if (!board) {
    return (
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '1.75rem clamp(1rem, 4vw, 2rem)' }}>
        <h1 style={{ fontSize: 'clamp(1.4rem, 4vw, 1.8rem)', fontWeight: 700, margin: 0, color: '#ffffff' }}>Morning Howl</h1>
        <p style={{ color: DWA.onDarkMuted }}>{error ? `Couldn't load today's Howl: ${error}` : 'Loading…'}</p>
      </main>
    );
  }

  const stayRows = board.stay_after;
  return (
    <main style={{ maxWidth: 1100, margin: '0 auto', padding: '1.75rem clamp(1rem, 4vw, 2rem) 6rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: 'clamp(1.4rem, 4vw, 1.8rem)', fontWeight: 700, margin: '0 0 0.35rem', color: '#ffffff' }}>Morning Howl</h1>
          <p style={{ margin: 0, color: DWA.onDarkMuted, fontSize: '0.95rem' }}>
            The daily stand-up. Randomize who goes first, then each speaker picks who's next.
          </p>
        </div>
        <span style={{ color: DWA.gold, fontWeight: 700, fontSize: '0.95rem' }}>{formatDay(board.date)}</span>
      </div>

      {error && (
        <p role="alert" style={{ margin: '1rem 0 0', color: '#fecaca', fontSize: '0.9rem' }}>
          {error}
        </p>
      )}

      <div
        style={{
          marginTop: '1.25rem',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', flex: '2 1 480px', minWidth: 0 }}>
          <Plate
            board={board}
            rolling={rolling}
            busy={busy}
            onStart={onStart}
            onUndo={() => run(undoTurn)}
            onCheckCalendar={() => run(checkCalendar)}
            onReset={() => {
              if (board.started || stayRows.length || board.out_member_ids.length) {
                if (!window.confirm("Reset today's Howl? This clears who has gone, who's out and the Stay After list.")) return;
              }
              run(resetHowl);
            }}
          />
          <section style={card}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h2 style={sectionTitle}>The pack</h2>
              <button type="button" style={linkButton} onClick={() => setEditingRoster(true)}>
                <FontAwesomeIcon icon={faPen} /> Edit team
              </button>
            </div>
            {board.members.length === 0 ? (
              <p style={{ margin: 0, color: DWA.textSecondary }}>No one on the team yet. Use Edit team to add people.</p>
            ) : (
              <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
                {board.members.map((m) => (
                  <Chip
                    key={m.id}
                    board={board}
                    memberId={m.id}
                    name={m.name}
                    flashing={flashId === m.id}
                    busy={busy}
                    onPick={(id) => run(() => pickNext(id))}
                    onUncheck={(id) => run(() => uncheckTurn(id))}
                    onToggleStay={(id, stay) => run(() => (stay ? setStayAfter(id, [], null) : clearStayAfter(id)))}
                    onToggleOut={(id, out) => run(() => setOut(id, out))}
                  />
                ))}
              </div>
            )}
          </section>
        </div>

        <aside style={{ ...card, flex: '1 1 260px' }} aria-label="Stay After">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h2 style={sectionTitle}>
              Stay After{' '}
              <span
                style={{
                  marginLeft: '0.35rem',
                  padding: '0.1rem 0.5rem',
                  borderRadius: 999,
                  background: stayRows.length ? DWA.gold : '#f3f4f6',
                  color: stayRows.length ? DWA.onGold : DWA.textSecondary,
                  letterSpacing: 0,
                }}
              >
                {stayRows.length}
              </span>
            </h2>
            <button type="button" style={linkButton} onClick={() => setStayFor('new')}>
              + Add
            </button>
          </div>
          {stayRows.length === 0 ? (
            <p style={{ margin: 0, color: DWA.textSecondary, fontSize: '0.9rem', lineHeight: 1.5 }}>
              Nobody yet. Click the pin on a name when someone's asked to stick around; add who they're staying with here.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {stayRows.map((s) => {
                const withNames = s.with_member_ids.map((id) => nameOf(board, id)).filter(Boolean);
                return (
                  <li
                    key={s.member_id}
                    data-testid={`howl-stay-${s.member_id}`}
                    style={{
                      borderLeft: `3px solid ${DWA.gold}`,
                      background: '#fafaf9',
                      borderRadius: DWA.radius,
                      padding: '0.55rem 0.6rem',
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'flex-start',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700 }}>{nameOf(board, s.member_id)}</div>
                      <div style={{ fontSize: '0.85rem', color: DWA.textSecondary }}>
                        {withNames.length ? `with ${joinNames(withNames)}` : 'with the team'}
                        {s.note ? ` · ${s.note}` : ''}
                      </div>
                    </div>
                    <button type="button" aria-label={`Edit ${nameOf(board, s.member_id)}`} style={linkButton} onClick={() => setStayFor(s.member_id)}>
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove ${nameOf(board, s.member_id)} from Stay After`}
                      style={linkButton}
                      disabled={busy}
                      onClick={() => run(() => clearStayAfter(s.member_id))}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>

      {stayFor !== null && (
        <StayAfterDialog
          board={board}
          memberId={stayFor === 'new' ? null : stayFor}
          busy={busy}
          onClose={() => setStayFor(null)}
          onSave={async (id, withIds, note) => {
            if (await run(() => setStayAfter(id, withIds, note.trim() || null))) setStayFor(null);
          }}
        />
      )}
      {editingRoster && (
        <RosterDialog
          board={board}
          busy={busy}
          onClose={() => setEditingRoster(false)}
          onSave={async (names) => {
            if (await run(() => saveRoster(names))) setEditingRoster(false);
          }}
        />
      )}
    </main>
  );
};

export const MorningHowlPage: React.FC = () => (
  <HubLayout>
    <GradientPage>
      <MorningHowlBoard />
    </GradientPage>
  </HubLayout>
);

export default MorningHowlPage;
